import React, { Fragment } from 'react';
import ReactDOM from 'react-dom'; //eslint-disable-line
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Dropdown, { DropdownTrigger, DropdownContent } from 'react-simple-dropdown';
import { Field } from 'redux-form';
import _find from 'lodash/find';
import _filter from 'lodash/filter';
import _head from 'lodash/head';
import _get from 'lodash/get';
import _isObject from 'lodash/isObject';

// Actions
import { actions as projectActions } from '../../../actions/projectActions';
import * as actions from '../../../reducers/pdp/actions';

// Components
import FoilField from './FoilField/FoilField';
import TextAlignmentField from './TextAlignmentField/TextAlignmentField';
import FontSizeField from './FontSizeField/FontSizeField';
import FontFamilyField from './FontFamilyField/FontFamilyField';
import FontStyleField from './FontStyleField/FontStyleField';
import LineHeightField from './LineHeightField/LineHeightField';
import FontFamilyIcon from '../../common/ui/icons/FontFamily';
import FontStyleIcon from '../../common/ui/icons/FontStyle';
import CopyIcon from '../../common/ui/icons/CopyIcon';

// Selectors
import {
  getActiveField,
  getProjectMedium,
  getCustomizationTypeUUIDs,
  getSize,
} from '../../../selectors/cardProjectSelector';
import { getPricing } from '../../../reducers/pricing/selectors';
import { getIsCustomFoilAvailable } from '../../../reducers/pdp/selectors';
import { getCustomizingCardIsLetterpress } from '../../../selectors/letterpress';

// Utilities
import { ptToPx } from '../../../util/index';
import { isOnlyFoil } from '../customizationUtils';

// Styles
import './ElementStyleEditor.less';

class ElementStyleEditor extends React.Component {
  constructor(props) {
    super(props);

    this.calculateDropdownYPosition = this.calculateDropdownYPosition.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleShowDropdown = this.handleShowDropdown.bind(this);
    this.handleHideDropdown = this.handleHideDropdown.bind(this);
    this.handleTimedDelete = this.handleTimedDelete.bind(this);
    this.cancelDelete = this.cancelDelete.bind(this);
    this.handleFontFamilyChange = this.handleFontFamilyChange.bind(this);
    this.handleFoilChange = this.handleFoilChange.bind(this);
    this.handleFontStyleChange = this.handleFontStyleChange.bind(this);
    this.handleFontDropdownState = this.handleFontDropdownState.bind(this);
    this.handleShowFontStylesDropdown = this.handleShowFontStylesDropdown.bind(this);
    this.handleHideFontStylesDropdown = this.handleHideFontStylesDropdown.bind(this);
    this.handleShowFoilDropdown = this.handleShowFoilDropdown.bind(this);
    this.handleHideFoilDropdown = this.handleHideFoilDropdown.bind(this);

    this.state = {
      dropdownsOpen: 0,
      shiftDistance: 0,
      deleteTimeoutId: null,
      fontStyleFill: '#336081',
      hasFontVariations: false,
    };
  }

  componentDidMount() {
    const { fonts } = this.props;
    // Need to init icon state to disable/enable if needed (depends on qty of variations available)
    if (fonts) {
      this.handleFontDropdownState();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // certain customizations are too wide, causing the dropdowns of the element style editor
    // to display off the page to the left. Fix this by shifting the element style editor to the right
    // whenever a dropdown is active
    const { activeField, fonts } = this.props;
    const { dropdownsOpen } = this.state;
    if (this.elementStyleEditor) {
      if (prevState.dropdownsOpen === 0 && dropdownsOpen > 0) {
        const viewportOffset = this.elementStyleEditor.getBoundingClientRect();
        const offsetRight = window.innerWidth - (viewportOffset.left + viewportOffset.width);
        // console.log('offsetRight 1: ', offsetRight);
        if (offsetRight < 50) {
          // eslint-disable-next-line
          this.setState({
            shiftDistance: 50 - offsetRight,
            // shiftDistance: 50 - offsetRight
          });
        }
      } else if (prevState.dropdownsOpen > 0 && dropdownsOpen === 0) {
        // eslint-disable-next-line
        this.setState({
          shiftDistance: 0,
        });
      }
    }
    if (prevProps.activeField !== activeField && fonts) {
      // Handle icon state when we get a different field id
      this.handleFontDropdownState();
    }
  }

  calculateDropdownYPosition(element) {
    const { cardHeight, showFullMenu } = this.props;

    // calculate vertical position & account for number of items
    const yPos = (2 * element.y + element.height) / 2;

    // update button counts as develpment progresses
    const fullMenuLength = 7;
    const shortenedMenuLength = 5;
    const totalButtons = showFullMenu ? fullMenuLength : shortenedMenuLength;

    // number of buttons multiplied by height of each button -> height of ElementStyleEditor
    const heightOfDropdown = totalButtons * 40;

    // use the y position and height of the active element and calculate the
    // average of the top y position and bottom y position (y position + height)
    // then we can subtract half of the height of ElementStyleEditor to get the correct
    // y position so that ElementStyleEditor will align vertically with active element
    let styleEditorYPosition = ptToPx(yPos) - heightOfDropdown / 2;

    // checks to make sure ElementStyleEditor is always within the vertical
    // bounds of the Preview
    if (styleEditorYPosition < 0) {
      // if the calculated y position is less than 0, we should use 0 so that
      // ElementStyleEditor does not overflow past the top of the preview
      styleEditorYPosition = 0;
    } else if (styleEditorYPosition + heightOfDropdown > ptToPx(cardHeight)) {
      // same thing for if the y position would cause the bottom of ElementStyleEditor
      // to overflow past the bottom of the preview
      styleEditorYPosition = ptToPx(cardHeight) - heightOfDropdown;
    }

    return { top: `${styleEditorYPosition}px` };
  }

  // hack: this is needed to override the customization input fields onBlur function from setting
  // activeField to null
  handleMouseDown(e) {
    const { element, setActiveField } = this.props;
    if (_isObject(e) && e.preventDefault) {
      e.preventDefault();
    }
    setActiveField(element.uuid);
  }

  handleShowDropdown() {
    this.setState(prevState => ({
      dropdownsOpen: prevState.dropdownsOpen + 1,
    }));
  }

  handleHideDropdown() {
    this.setState(prevState => ({
      dropdownsOpen: prevState.dropdownsOpen - 1,
    }));
  }

  handleShowFoilDropdown() {
    this.setState(prevState => ({
      dropdownsOpen: prevState.dropdownsOpen + 1,
    }));
  }

  handleHideFoilDropdown() {
    this.setState(prevState => ({
      dropdownsOpen: prevState.dropdownsOpen - 1,
    }));
  }

  handleShowFontStylesDropdown() {
    this.setState(prevState => ({
      dropdownsOpen: prevState.dropdownsOpen + 1,
    }));
  }

  handleHideFontStylesDropdown() {
    this.setState(prevState => ({
      dropdownsOpen: prevState.dropdownsOpen - 1,
    }));
  }

  handleTimedDelete() {
    const { element, handleDelete } = this.props;
    this.setState({
      deleteTimeoutId: setTimeout(() => {
        handleDelete(element.uuid);
      }, 2000),
    });
  }

  cancelDelete() {
    const { deleteTimeoutId } = this.state;
    if (deleteTimeoutId) {
      clearTimeout(deleteTimeoutId);
      this.setState({
        deleteTimeoutId: null,
      });
    }
  }

  handleFoilChange(event, value) {
    // TODO: there is a weird race condition here that is not tracking the foiled elements properly
    const {
      element,
      handleChange,
      addToFoilHistory,
      foiledElements,
      handleUpdateFoil,
      customizationCardType,
    } = this.props;
    if (_isObject(event) && event.preventDefault) {
      event.preventDefault();
    }
    const selectedFoil = value;

    if (selectedFoil === 'none') {
      const lastFoil = isOnlyFoil(foiledElements, element);
      handleChange([
        { elementUUID: element.uuid, fieldName: 'printing_technique', value: 'DIGITAL' },
      ]);
      // if last foil need to change the variation and change the foil history
      if (lastFoil) {
        handleUpdateFoil('none');
      }
    } else if (selectedFoil && selectedFoil !== 'none') {
      handleChange([
        { elementUUID: element.uuid, fieldName: 'printing_technique', value: 'CUSTOM_FOIL' },
      ]);
      handleUpdateFoil(selectedFoil);
      addToFoilHistory(selectedFoil, customizationCardType);
    }
  }

  handleFontFamilyChange(event, value) {
    const { element, fonts, handleChange } = this.props;
    const { raw: rawFonts } = fonts;
    let selectedFontId;
    if (_isObject(event) && event.preventDefault) {
      event.preventDefault();
    }
    // This is sort of a hack but is only to apply the regular font when
    // one is chosen from the list as the list contains all unique fonts
    // regardless of variations, this results sometimes in having the bold or italic variation as default
    // but we always want the refgular one to start the customization
    const hasVariations = value && value.variations && value.variations.length > 1;
    if (hasVariations) {
      const fontVariation = _find(value.variations, { displayName: 'Regular' });
      selectedFontId = fontVariation ? fontVariation.id : value.id;
      this.setState({ fontStyleFill: '#336081', hasFontVariations: true });
    } else {
      selectedFontId = value.id;
      this.setState({ fontStyleFill: '#D9D9D9', hasFontVariations: false });
    }
    const selectedFont = _find(rawFonts, { id: selectedFontId });
    if (selectedFont) {
      // Both requests are needed here, one for local change via form (font)
      // and one for the api call (font_id)
      handleChange([
        { elementUUID: element.uuid, fieldName: 'font', value: selectedFont },
        { elementUUID: element.uuid, fieldName: 'font_id', value: selectedFont.id },
      ]);
    }
  }

  handleFontStyleChange(event, value) {
    const { element, fonts, handleChange } = this.props;
    const { raw: rawFonts } = fonts;
    if (_isObject(event) && event.preventDefault) {
      event.preventDefault();
    }
    const selectedFont = _find(rawFonts, { id: value.id });
    if (selectedFont) {
      // Same here, both requests are needed here, one for local change via form (font)
      // and one for the api call (font_id)
      handleChange([
        { elementUUID: element.uuid, fieldName: 'font', value: selectedFont },
        { elementUUID: element.uuid, fieldName: 'font_id', value: selectedFont.id },
      ]);
    }
  }

  handleFontDropdownState() {
    // TODO: font_id is not set on new text elements fallback this with (element.font_id || element.font.id)
    // but it may be better to have that logic in reducer, see case ActionTypes.RECEIVE_ADD_TEXT_ELEMENT
    // in cardProject reducer
    const { element, fonts } = this.props;
    const mappedFont = _find(fonts.mapped, { id: element.font_id || element.font.id });
    // If we have a first level match we check if it has variations
    if (mappedFont && mappedFont.variations && mappedFont.variations.length > 1) {
      this.setState({ hasFontVariations: true, fontStyleFill: '#336081' });
    } else {
      // If match is second level (inside variations array) we filter via variations to find match
      const unmappedFont = _head(
        _filter(fonts.mapped, font =>
          _find(font.variations, { id: element.font_id || element.font.id })
        )
      );
      if (unmappedFont && unmappedFont.variations && unmappedFont.variations.length > 1) {
        this.setState({ hasFontVariations: true, fontStyleFill: '#336081' });
      } else {
        this.setState({ hasFontVariations: false, fontStyleFill: '#D9D9D9' });
      }
    }
  }

  render() {
    const {
      element,
      fonts,
      showFullMenu,
      showLineHeight = true,
      showFontStyle = true,
      showAlignment = true,
      showFontSize = true,
      handleChange,
      handleCloneElement,
      showNotesFonts,
      disableDelete,
      pricing,
      projectAvailableOptions,
      medium,
      customizationFoilColor,
      size,
      showFoilOnThisStep,
      foilAvailable,
      isLetterpress,
      handleDelete,
      projectType,
      customizationSize,
    } = this.props;
    const { fontStyleFill, hasFontVariations, shiftDistance, deleteTimeoutId } = this.state;

    const minFontSize =
      isLetterpress && typeof element.font.letterpress_minimum_font_size === 'number'
        ? element.font.letterpress_minimum_font_size
        : 6;

    return (
      <Fragment>
        <div
          className="element-style-editor element-style-editor--right"
          style={{
            right: `${-50 + shiftDistance}px`,
            ...this.calculateDropdownYPosition(element),
          }}
          ref={el => {
            this.elementStyleEditor = el;
          }}
          onMouseDown={this.handleMouseDown}
        >
          {/* Foil selection */}
          {showFoilOnThisStep && foilAvailable && customizationSize !== 'postcard' && (
            <Dropdown
              onShow={this.handleShowFontStylesDropdown}
              onHide={this.handleHideFontStylesDropdown}
              ref={el => {
                this.foilElement = el;
              }}
              className="foil-dropdown"
            >
              <DropdownTrigger className="foil-dropdown__trigger">
                <div className="edit-element-property__label">Foil</div>
              </DropdownTrigger>
              <DropdownContent
                className="foil-dropdown__content"
                ref={el => {
                  this.foilDropdownContent = el;
                }}
                style={{ right: '-230px', top: '100px' }}
              >
                <Field
                  name={`${element.uuid}.foil_selection`}
                  component={FoilField}
                  activeFoilColor={customizationFoilColor}
                  printingTechnique={element.printing_technique}
                  availableFoil={
                    projectAvailableOptions &&
                    projectAvailableOptions['foil-color'] &&
                    projectAvailableOptions['foil-color'].options
                  }
                  onChange={(event, value) => this.handleFoilChange(event, value)}
                  pricing={_get(pricing, [projectType, medium.toLowerCase(), size, 'foil-color'])}
                />
                <div className="dropdown__caret" style={{ top: '20px' }}>
                  <i className="glyphicon glyphicon-triangle-left" />
                </div>
              </DropdownContent>
            </Dropdown>
          )}
          {/* Font family selection */}
          <Dropdown
            onShow={this.handleShowFontStylesDropdown}
            onHide={this.handleHideFontStylesDropdown}
            ref={el => {
              this.fontFamilyElement = el;
            }}
            className="font-family-dropdown"
          >
            <DropdownTrigger className="font-family-dropdown__trigger">
              <div className="edit-element-property__label">
                <FontFamilyIcon
                  fill={
                    this.fontFamilyElement && this.fontFamilyElement.isActive() ? '#FFF' : '#336081'
                  }
                />
              </div>
            </DropdownTrigger>
            <DropdownContent
              className="font-family-dropdown__content"
              ref={el => {
                this.fontFamilyDropdownContent = el;
              }}
              style={{ top: '120pt' }}
            >
              <Field
                name={`${element.uuid}.font_family_selection`}
                component={FontFamilyField}
                availableFonts={fonts}
                element={element}
                onChange={(event, value) => this.handleFontFamilyChange(event, value)}
                showNotesFonts={showNotesFonts}
              />
              <div className="dropdown__caret">
                <i className="glyphicon glyphicon-triangle-left" />
              </div>
            </DropdownContent>
          </Dropdown>
          {/* Font style selection */}
          {showFontStyle && (
            <Dropdown
              onShow={this.handleShowDropdown}
              onHide={this.handleHideDropdown}
              ref={el => {
                this.fontStyleElement = el;
              }}
              disabled={!hasFontVariations}
              className="font-style-dropdown"
            >
              <DropdownTrigger className="font-style-dropdown__trigger">
                <div className="edit-element-property__label">
                  <FontStyleIcon
                    fill={
                      this.fontStyleElement && this.fontStyleElement.isActive()
                        ? '#FFF'
                        : fontStyleFill
                    }
                  />
                </div>
              </DropdownTrigger>
              <DropdownContent className="font-style-dropdown__content">
                <Field
                  name={`${element.uuid}.font_style_selection`}
                  component={FontStyleField}
                  availableFonts={fonts}
                  element={element}
                  onChange={(event, value) => this.handleFontStyleChange(event, value)}
                />
                <div className="dropdown__caret">
                  <i className="glyphicon glyphicon-triangle-left" />
                </div>
              </DropdownContent>
            </Dropdown>
          )}
          {/* Alignment */}
          {showAlignment && (
            <Dropdown onShow={this.handleShowDropdown} onHide={this.handleHideDropdown}>
              <DropdownTrigger>
                <div className="edit-element-property__label">
                  <i className="zolaicon zolaicon-text-align-left" />
                </div>
              </DropdownTrigger>
              <DropdownContent>
                <Field
                  name={`${element.uuid}.horizontal_alignment`}
                  component={TextAlignmentField}
                  onChange={(e, value) => {
                    if (_isObject(e) && e.preventDefault) {
                      e.preventDefault();
                    }
                    handleChange({
                      elementUUID: element.uuid,
                      fieldName: 'horizontal_alignment',
                      value,
                    });
                  }}
                />
                <div className="dropdown__caret">
                  <i className="glyphicon glyphicon-triangle-left" />
                </div>
              </DropdownContent>
            </Dropdown>
          )}
          {/* Font Size */}
          {showFontSize && (
            <Dropdown onShow={this.handleShowDropdown} onHide={this.handleHideDropdown}>
              <DropdownTrigger>
                <div className="edit-element-property__label">
                  <i className="zolaicon zolaicon-font-size" />
                </div>
              </DropdownTrigger>
              <DropdownContent>
                <Field
                  name={`${element.uuid}.font_size`}
                  component={FontSizeField}
                  minSize={minFontSize}
                  maxSize={200}
                  onChange={(e, value, prevValue) => {
                    if (_isObject(e) && e.preventDefault) {
                      e.preventDefault();
                    }
                    // change in font_size
                    const delta = value - prevValue;
                    // Don't do anything if the size hasn't actually changed.
                    if (delta === 0) return;
                    // increase/decrease line_height along with changes to font_size
                    handleChange([
                      { elementUUID: element.uuid, fieldName: 'font_size', value },
                      {
                        elementUUID: element.uuid,
                        fieldName: 'line_height',
                        value: element.line_height + delta,
                      },
                    ]);
                  }}
                />
                <div className="dropdown__caret">
                  <i className="glyphicon glyphicon-triangle-left" />
                </div>
              </DropdownContent>
            </Dropdown>
          )}
          {/* Line Height */}
          {showLineHeight && (
            <Dropdown onShow={this.handleShowDropdown} onHide={this.handleHideDropdown}>
              <DropdownTrigger>
                <div className="edit-element-property__label">
                  <i className="zolaicon zolaicon-line-height" />
                </div>
              </DropdownTrigger>
              <DropdownContent>
                <Field
                  name={`${element.uuid}.line_height`}
                  component={LineHeightField}
                  minSize={element.font_size}
                  maxSize={250}
                  onChange={(e, value) => {
                    if (_isObject(e) && e.preventDefault) {
                      e.preventDefault();
                    }
                    if (value >= element.font_size) {
                      handleChange({
                        elementUUID: element.uuid,
                        fieldName: 'line_height',
                        value,
                      });
                    }
                  }}
                />
                <div className="dropdown__caret">
                  <i className="glyphicon glyphicon-triangle-left" />
                </div>
              </DropdownContent>
            </Dropdown>
          )}
          {showFullMenu && (
            <div
              className="duplicate-trigger duplicate-trigger-keypress"
              onClick={() => handleCloneElement(element)}
            >
              <CopyIcon />
              {/* <i className="glyphicon glyphicon-duplicate" /> */}
            </div>
          )}
          {
            // delete element button
            showFullMenu && (
              <div
                className={`delete-trigger delete-trigger-keypress ${
                  disableDelete ? 'delete-trigger--is-disabled' : ''
                }`}
                onClick={() => handleDelete(element.uuid)}
              >
                <i className="zolaicon zolaicon-trash" />
                {deleteTimeoutId && <div className="delete-progress" />}
              </div>
            )
          }
        </div>
      </Fragment>
    );
  }
}

ElementStyleEditor.propTypes = {
  activeField: PropTypes.string,
  element: PropTypes.shape({}),
  fonts: PropTypes.shape({
    mapped: PropTypes.arrayOf(PropTypes.shape({})),
    mappedNote: PropTypes.arrayOf(PropTypes.shape({})),
    raw: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  cardHeight: PropTypes.number,
  setActiveField: PropTypes.func,
  showFullMenu: PropTypes.bool,
  showFontStyle: PropTypes.bool,
  showFontSize: PropTypes.bool,
  showAlignment: PropTypes.bool,
  showLineHeight: PropTypes.bool,
  disableDelete: PropTypes.bool,
  handleChange: PropTypes.func,
  handleDelete: PropTypes.func,
  handleCloneElement: PropTypes.func,
  projectAvailableOptions: PropTypes.shape({}),
  showFoilOnThisStep: PropTypes.bool,
  foilAvailable: PropTypes.bool,
  // TODO do we need this for RSVP?
  customizationCardType: PropTypes.string,
  customizationFoilColor: PropTypes.string,
  projectType: PropTypes.string,
  handleUpdateFoil: PropTypes.func,
  customizationSize: PropTypes.string,
};

const mapStateToProps = state => ({
  activeField: getActiveField(state),
  fetchCardSuite: actions.fetchCardSuite,
  pricing: getPricing(state),
  medium: getProjectMedium(state),
  size: getSize(state),
  currentCustomization: getCustomizationTypeUUIDs(state),
  foilAvailable: getIsCustomFoilAvailable(state) && getProjectMedium(state) === 'PAPER',
  isLetterpress: getCustomizingCardIsLetterpress(state),
});

const mapDispatchToProps = dispatch => {
  const setActiveField = name => dispatch(projectActions.setActiveField(name));

  return {
    setActiveField,
    updateCustomizationVariation: (customizationUUID, nextOptionValues, isTestProject) =>
      dispatch(
        projectActions.updateCustomizationVariation(
          customizationUUID,
          nextOptionValues,
          isTestProject
        )
      ),
    setOptionValue: newVal => dispatch(actions.setOptionValue(newVal)),
    addToFoilHistory: (newFoil, customizationCardType) =>
      dispatch(projectActions.addToFoilHistory(newFoil, customizationCardType)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ElementStyleEditor);
